/**
 * Centralized route helper. Use these builders for all internal navigation
 * so future restructures are one-edit changes.
 *
 * URL conventions:
 *   - The operator surface lives at the root (`/`, `/inbox`, `/chat`, …).
 *   - The console lives under `/c`; spaces use `/c/s/:spaceId/...`.
 *   - Collection list / `new` / tab paths keep the full word (`/agents`,
 *     `/agents/new`, `/agents/schedules`) so the URL self-documents what
 *     surface the user is on.
 *   - When a URL points at a specific entity by id, the parent collection
 *     collapses to a short alias so deep links stay scannable:
 *       agents/:id        → a/:id
 *       skills/:id        → sk/:id
 *       tools/:id         → t/:id
 *       integrations/:id  → i/:id
 *       tools/gateways/:id → tools/gw/:id
 *       tools/mcp-servers/:id → tools/mcp/:id
 *       connections/emails/:id → connections/em/:id
 *
 *   - Runs are cross-space (`/c/runs`, `/c/r/:id`) — they aggregate across
 *     every space the user belongs to, so they don't sit under `/c/s/:spaceId`.
 */
export const routes = {
  signin: '/signin',
  // Cookie-reset recovery — wipes JS-visible cookies + storage and
  // redirects back to /signin?reset=1. Lets stranded users self-recover
  // from a CloudFront 431 caused by accumulated cookies.
  signinReset: '/signin/reset',
  signup: '/signup',
  forgotPassword: '/forgot-password',
  // IdP-initiated SSO entry. The Okta catalog tile's launch URL points
  // here so we can auto-login users whose email we remember locally.
  sso: '/sso',
  // Public landing for AgentCore's post-consent redirect (MCP server OAuth).
  // Whitelisted on the AgentCore workload identity in stg/dev.
  oauthConnected: '/oauth/connected',

  operator: {
    root: '/',
    overview: '/',
    inbox: '/inbox',
    chats: '/chat',
    chatNew: '/chat/new',
    chat: (conversationId: string) => `/chat/${conversationId}`,
    knowledge: '/knowledge',
    /** Browser at a KB's root. */
    knowledgeBase: (kbId: string) => `/knowledge/${kbId}`,
    /** Browser at a folder inside a KB. The folder path is passed unencoded
     *  here; segments get URL-encoded individually so the React Router URL
     *  bar survives names with spaces or slashes. */
    knowledgeBasePath: (kbId: string, folderPath: string) =>
      `/knowledge/${kbId}/p/${folderPath
        .split('/')
        .filter(Boolean)
        .map(encodeURIComponent)
        .join('/')}`,
    /** Deep link to a specific file open in the preview pane. Uses the file's
     *  short id (not its path) so shared URLs stay compact; the page resolves
     *  the id back to a path on load to reveal + open it. */
    knowledgeBaseFile: (kbId: string, fileId: string) =>
      `/knowledge/${kbId}/f/${fileId}`,
    activity: '/activity',
    builder: '/builder',
  },

  console: {
    root: '/c',
    settings: '/c/settings',
    events: '/c/events',
    runs: '/c/runs',
    run: (runId: string) => `/c/r/${runId}`,
    /** Runs list pre-filtered to one agent. The Runs page reads `agentId`
     *  from the query string, so this is a shareable deep link. */
    runsForAgent: (agentId: string) =>
      `/c/runs?agentId=${encodeURIComponent(agentId)}`,
    credentials: '/c/credentials',
    // Org-wide usage analytics. Admin-only (gated by <RequireOrgAdmin>); a
    // top-level entry rather than a /c/org tab so the wide dot plot gets full
    // width.
    analytics: '/c/analytics',

    spaceRoot: (spaceId: string) => `/c/s/${spaceId}`,

    spaceAgents: (spaceId: string) => `/c/s/${spaceId}/agents`,
    spaceAgentSchedules: (spaceId: string) => `/c/s/${spaceId}/agents/schedules`,
    spaceAgentSubscriptions: (spaceId: string) => `/c/s/${spaceId}/agents/subscriptions`,
    spaceAgentNew: (spaceId: string) => `/c/s/${spaceId}/agents/new`,
    spaceAgent: (spaceId: string, agentId: string) =>
      `/c/s/${spaceId}/a/${agentId}`,
    spaceAgentEdit: (spaceId: string, agentId: string) =>
      `/c/s/${spaceId}/a/${agentId}/edit`,

    spaceSkills: (spaceId: string) => `/c/s/${spaceId}/skills`,
    spaceSkillNew: (spaceId: string) => `/c/s/${spaceId}/skills/new`,
    spaceSkill: (spaceId: string, skillId: string) =>
      `/c/s/${spaceId}/sk/${skillId}`,
    spaceSkillEdit: (spaceId: string, skillId: string) =>
      `/c/s/${spaceId}/sk/${skillId}/edit`,

    spacePlugins: (spaceId: string) => `/c/s/${spaceId}/plugins`,
    spacePluginNew: (spaceId: string) => `/c/s/${spaceId}/plugins/new`,
    spacePlugin: (spaceId: string, pluginId: string) =>
      `/c/s/${spaceId}/plg/${pluginId}`,

    spaceTools: (spaceId: string) => `/c/s/${spaceId}/tools`,
    spaceToolsGateways: (spaceId: string) => `/c/s/${spaceId}/tools/gateways`,
    spaceToolsGateway: (spaceId: string, gatewayId: string) =>
      `/c/s/${spaceId}/tools/gw/${gatewayId}`,
    spaceToolsMcpServers: (spaceId: string) => `/c/s/${spaceId}/tools/mcp-servers`,
    spaceToolsMcpServerNew: (spaceId: string) =>
      `/c/s/${spaceId}/tools/mcp-servers/new`,
    spaceToolsMcpServer: (spaceId: string, serverId: string) =>
      `/c/s/${spaceId}/tools/mcp/${serverId}`,
    // Space-side read-only view of an org-installed MCP server. Admin actions
    // (edit, sync, delete) live under /c/org/mcp/...
    spaceToolsOrgMcpServer: (spaceId: string, serverId: string) =>
      `/c/s/${spaceId}/tools/org-mcp/${serverId}`,
    spaceToolNew: (spaceId: string) => `/c/s/${spaceId}/tools/new`,
    spaceTool: (spaceId: string, toolId: string) =>
      `/c/s/${spaceId}/t/${toolId}`,
    spaceToolEdit: (spaceId: string, toolId: string) =>
      `/c/s/${spaceId}/t/${toolId}/edit`,

    spaceKbs: (spaceId: string) => `/c/s/${spaceId}/kbs`,
    /** KB browser is nested under the list URL so list + inline browser
     *  share one page; no alias-collapse needed. */
    spaceKb: (spaceId: string, kbId: string) => `/c/s/${spaceId}/kbs/${kbId}`,
    spaceKbPath: (spaceId: string, kbId: string, folderPath: string) =>
      `/c/s/${spaceId}/kbs/${kbId}/p/${folderPath
        .split('/')
        .filter(Boolean)
        .map(encodeURIComponent)
        .join('/')}`,
    /** Deep link to a file open in the preview pane (short file id). */
    spaceKbFile: (spaceId: string, kbId: string, fileId: string) =>
      `/c/s/${spaceId}/kbs/${kbId}/f/${fileId}`,

    spaceConnectionEmails: (spaceId: string) => `/c/s/${spaceId}/connections/emails`,
    spaceConnectionEmailNew: (spaceId: string) =>
      `/c/s/${spaceId}/connections/emails/new`,
    spaceConnectionEmail: (spaceId: string, emailId: string) =>
      `/c/s/${spaceId}/connections/em/${emailId}`,
    spaceConnectionEmailEdit: (spaceId: string, emailId: string) =>
      `/c/s/${spaceId}/connections/em/${emailId}/edit`,

    spaceIntegrations: (spaceId: string) => `/c/s/${spaceId}/integrations`,
    spaceIntegrationNew: (spaceId: string) => `/c/s/${spaceId}/integrations/new`,
    // Google Workspace (Gmail/Chat) connect — not the generic webhook creator.
    spaceGoogle: (spaceId: string) => `/c/s/${spaceId}/google`,
    spaceIntegration: (spaceId: string, integrationId: string) =>
      `/c/s/${spaceId}/i/${integrationId}`,
    // Space-side view of an org-level integration whose routing rules
    // forward events here. Read-only — admin actions live under /c/org/i/...
    spaceSubscription: (spaceId: string, integrationId: string) =>
      `/c/s/${spaceId}/sub/${integrationId}`,

    spaceSandbox: (spaceId: string) => `/c/s/${spaceId}/sandbox`,
    spaceSandboxFor: (spaceId: string, agentId: string) =>
      `/c/s/${spaceId}/sandbox/${agentId}`,

    spaceConversations: (spaceId: string) => `/c/s/${spaceId}/conversations`,
    spaceConversation: (spaceId: string, conversationId: string) =>
      `/c/s/${spaceId}/conv/${conversationId}`,

    spaceManage: (spaceId: string) => `/c/s/${spaceId}/space`,
    spaceMembers: (spaceId: string) => `/c/s/${spaceId}/space/members`,
    spaceAutoJoin: (spaceId: string) => `/c/s/${spaceId}/space/auto-join`,
    spaceLabels: (spaceId: string) => `/c/s/${spaceId}/space/labels`,

    // Org-scoped (admin-only). Mirrors the /c/s/:spaceId pattern but is
    // gated by user.role === 'admin' via <RequireOrgAdmin>.
    org: {
      root: '/c/org',
      integrations: '/c/org/integrations',
      integrationNew: '/c/org/integrations/new',
      googleWorkspace: '/c/org/google-workspace',
      integration: (integrationId: string) => `/c/org/i/${integrationId}`,
      integrationRules: (integrationId: string) => `/c/org/i/${integrationId}/rules`,
      integrationRuleNew: (integrationId: string) =>
        `/c/org/i/${integrationId}/rules/new`,
      mcpServers: '/c/org/mcp-servers',
      mcpServerNew: '/c/org/mcp-servers/new',
      mcpServer: (serverId: string) => `/c/org/mcp/${serverId}`,
      mcpServerEdit: (serverId: string) => `/c/org/mcp/${serverId}/edit`,
      pluginCatalog: '/c/org/plugin-catalog',
      identityProviders: '/c/org/identity-providers',
      knowledgeBases: '/c/org/kbs',
      knowledgeBase: (kbId: string) => `/c/org/kbs/${kbId}`,
      knowledgeBasePath: (kbId: string, folderPath: string) =>
        `/c/org/kbs/${kbId}/p/${folderPath
          .split('/')
          .filter(Boolean)
          .map(encodeURIComponent)
          .join('/')}`,
      /** Deep link to a file open in the preview pane (short file id). */
      knowledgeBaseFile: (kbId: string, fileId: string) =>
        `/c/org/kbs/${kbId}/f/${fileId}`,
    },
  },
} as const;

/**
 * Compute the destination path when the user swaps active space via the
 * switcher. Keeps the first section segment (agents, skills, …) and drops
 * everything after it — entity ids from the old space don't exist in the new
 * one, so we fall back to the section list.
 *
 * Section aliases (a/sk/t/i/r) are mapped back to their plural list form so
 * the destination is the section's index page rather than a stale entity url.
 */
export function switchSpace(currentPath: string, newSpaceId: string): string {
  // Org-scoped URLs aren't space-relative — don't rewrite them.
  if (currentPath.startsWith('/c/org')) {
    return routes.console.spaceRoot(newSpaceId) + '/agents';
  }
  const m = currentPath.match(/^\/c\/s\/[^/]+(?:\/([^/]+))?/);
  const segment = m?.[1] ?? 'agents';
  const sectionList: Record<string, string> = {
    a: 'agents',
    sk: 'skills',
    plg: 'plugins',
    t: 'tools',
    i: 'integrations',
    sub: 'integrations',
    conv: 'conversations',
    // Tabs and full-word collections keep their identity.
    agents: 'agents',
    skills: 'skills',
    plugins: 'plugins',
    tools: 'tools',
    kbs: 'kbs',
    connections: 'connections/emails',
    integrations: 'integrations',
    sandbox: 'sandbox',
    conversations: 'conversations',
    space: 'space',
  };
  const section = sectionList[segment] ?? 'agents';
  return `/c/s/${newSpaceId}/${section}`;
}
